import React from 'react';
import { apiPost } from '../../../lib/api';
import { enqueue } from '../../../lib/offlineQueue';
import { toast } from 'sonner';

export const UNIT_OPTIONS = ['bag', 'MT', 'cum', 'sqm', 'rmt', 'nos', 'litre', 'kg'];

/** POSTs a department section save. On a real network failure (no response
 *  reached the server — offline or unreachable) it queues the SAME
 *  per-section endpoint via the offline queue instead of failing, so the
 *  mobile console can save on-site with no signal. A definitive server
 *  rejection (validation error, 4xx) is NOT queued — it's shown as-is. */
export async function postSection(projectId, date, dept, payload) {
  try {
    const res = await apiPost(`/dpr/${encodeURIComponent(projectId)}/${encodeURIComponent(date)}/section/${dept}`, payload);
    return { data: res, offline: false };
  } catch (e) {
    if (!e?.response) {
      await enqueue('dpr_section', { label: `DPR · ${dept}`, payload: { projectId, date, dept, data: payload } });
      return { data: { __offline_queued: true, dept, data: payload }, offline: true };
    }
    throw e;
  }
}

export async function saveSection(projectId, date, dept, payload, onSaved, deptLabel, setSaving) {
  setSaving(true);
  try {
    const { data, offline } = await postSection(projectId, date, dept, payload);
    onSaved(data);
    toast.success(offline ? `${deptLabel} section saved offline — will sync when back online` : `${deptLabel} section saved`);
  } catch {
    toast.error('Save failed');
  } finally { setSaving(false); }
}

export function SectionShell({ deptLabel, dept, children, onSave, saving, readOnly = false }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid={`dpr-section-${dept}`}>
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
        <span className="font-display font-bold text-sm uppercase tracking-wider text-slate-700">
          {deptLabel}
        </span>
        {readOnly && (
          <span className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-400 border border-slate-200 rounded-sm px-2 py-0.5" data-testid={`dpr-${dept}-readonly-badge`}>
            Read only
          </span>
        )}
      </div>
      <div className="p-4 space-y-3">
        {children}
        {!readOnly && (
          <div className="flex justify-end">
            <button
              onClick={onSave}
              disabled={saving}
              className="text-sm font-display font-semibold uppercase tracking-wider px-4 py-2 rounded-sm bg-[#3b4872] text-white disabled:bg-slate-300"
              data-testid={`dpr-${dept}-save`}
            >
              {saving ? 'Saving…' : 'Save section'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function NumField({ label, value, onChange, min = 0, max, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">{label}</label>
      <input
        type="number" min={min} max={max} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-200 rounded-sm text-sm px-3 py-2 tabular-nums"
        data-testid={testid}
      />
    </div>
  );
}

export function TextAreaField({ label, value, onChange, rows = 2, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full border border-slate-200 rounded-sm text-sm px-3 py-2"
        data-testid={testid}
      />
    </div>
  );
}

export function MaterialRows({ label, rows, setRows, lastCol, testid }) {
  const blank = { description: '', qty: 0, unit: 'bag', [lastCol.key]: '' };
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</label>
        <button
          onClick={() => setRows([...rows, { ...blank }])}
          className="text-xs uppercase tracking-wider text-[#3b4872] font-semibold"
          data-testid={`${testid}-add`}
        >
          + Add
        </button>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && (
          <div className="text-[11px] text-slate-400 py-2 text-center border border-dashed border-slate-200 rounded-sm">
            No rows — click "+ Add"
          </div>
        )}
        {rows.map((r, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`${testid}-row-${i}`}>
            <input
              value={r.description}
              onChange={(e) => setRows(rows.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))}
              placeholder="Description"
              className="flex-1 min-w-[140px] border border-slate-200 rounded-sm text-sm px-2 py-1.5"
              data-testid={`${testid}-desc-${i}`}
            />
            <input
              type="number" step="any" value={r.qty}
              onChange={(e) => setRows(rows.map((x, idx) => idx === i ? { ...x, qty: e.target.value } : x))}
              placeholder="Qty"
              className="w-20 border border-slate-200 rounded-sm text-sm px-2 py-1.5 text-right tabular-nums"
              data-testid={`${testid}-qty-${i}`}
            />
            <select
              value={r.unit}
              onChange={(e) => setRows(rows.map((x, idx) => idx === i ? { ...x, unit: e.target.value } : x))}
              className="w-24 border border-slate-200 rounded-sm text-sm px-2 py-1.5 bg-white"
              data-testid={`${testid}-unit-${i}`}
            >
              {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
            <input
              value={r[lastCol.key] || ''}
              onChange={(e) => setRows(rows.map((x, idx) => idx === i ? { ...x, [lastCol.key]: e.target.value } : x))}
              placeholder={lastCol.label}
              className="flex-1 min-w-[120px] border border-slate-200 rounded-sm text-sm px-2 py-1.5"
              data-testid={`${testid}-${lastCol.key}-${i}`}
            />
            <button onClick={() => setRows(rows.filter((_, idx) => idx !== i))} className="text-rose-600 text-sm">×</button>
          </div>
        ))}
      </div>
    </div>
  );
}
